import { IS_TEACHER } from '@constants';
import { TABLE_STATUSES, WORD_STATUSES } from '@constants/gameConfigs';
import { useGameContext } from 'context/GameContext';
import { useStudentInGameInfo, useTeacherInGameInfo } from 'hooks';
import React, { memo, useCallback, useMemo } from 'react';
import trans from 'translation';
import { KButton, KColors, KContainer, KLabel } from 'uikit';

import InGameImage from '../InGame.Image';

interface IProps {
  status?: TABLE_STATUSES;
}

const WaitingForStart = ({ status }: IProps) => {
  const {
    onStartWord,
    onEmergencyEnd,
    isTaboo,
    isJumble,
    isHotseat,
    roomInfo
  } = useGameContext();

  const { teams = [], users = [] } = roomInfo || {};

  const isWaiting = status === TABLE_STATUSES.WAITING;

  const { runningTeam } = useTeacherInGameInfo();
  const { opposingTeam } = useStudentInGameInfo();

  const curTeam = useMemo(
    () => (IS_TEACHER ? runningTeam : opposingTeam),
    [opposingTeam, runningTeam]
  );

  const wordIndex = useMemo(() => {
    if (curTeam) {
      const words = curTeam.words ?? [];
      const index = words.findIndex(
        w => w.status === WORD_STATUSES.NOT_STARTED_YET
      );
      if (index !== -1) {
        return index + 1;
      }
    }
    return 1;
  }, [curTeam]);

  const title = useMemo(() => {
    if (!isWaiting) {
      return trans('common.waiting_for_students');
    }

    if (isJumble) {
      return trans('common.start_game');
    }

    return (
      `${trans('common.start_word') + ` ${wordIndex}`}` +
      (!isTaboo ? '' : ` for ${curTeam?.name ?? ''}`)
    );
  }, [curTeam?.name, isJumble, isTaboo, isWaiting, wordIndex]);

  const renderHotseatContent = useCallback(() => {
    return (
      <KContainer.View marginT="1.5rem" dp="flex" gap="0.5rem">
        {teams
          .map(t => {
            const describerId = t.words?.[wordIndex - 1]?.describerId;
            const user = users.find(u => u.id === describerId);

            if (user) {
              return (
                <KLabel.Paragraph
                  typo="Text3xLgMedium"
                  color={KColors.white}
                  textAlign="center"
                  key={t.id}
                >
                  {t.name} {trans('common.guesser')} - {user.username}
                </KLabel.Paragraph>
              );
            }

            return null;
          })
          .filter(Boolean)}
      </KContainer.View>
    );
  }, [teams, users, wordIndex]);

  const needEmergencyEnd = IS_TEACHER && isHotseat;

  return (
    <KContainer.View alignItems padding="1.5rem">
      <InGameImage
        uri="waiting-for-start.webp"
        background={KColors.primary.normal}
        justifyContent={isHotseat ? 'center' : undefined}
      >
        {isHotseat && renderHotseatContent()}

        <KContainer.View
          marginT="3rem"
          row
          alignItems
          justifyContent="space-around"
          flex
          alignSelf="stretch"
        >
          <KButton.Solid
            title={title}
            kind="gray"
            width="100%"
            background={KColors.white}
            textColor={KColors.primary.normal}
            onPress={onStartWord}
            disabled={!isWaiting}
          />

          {needEmergencyEnd ? <KContainer.View width={'1rem'} /> : null}

          {needEmergencyEnd && (
            <KButton.Solid
              width={'100%'}
              title={trans('common.emergency_end')}
              onPress={() => onEmergencyEnd()}
              kind="gray"
              background={KColors.white}
              textColor={KColors.primary.normal}
            />
          )}
        </KContainer.View>
      </InGameImage>
    </KContainer.View>
  );
};

WaitingForStart.displayName = 'Containers.InGame.WaitingForStart';

export default memo(WaitingForStart);
